import {
  type GetPresentationsByDemoArgs,
  useLazyGetPresentationsByDemoQuery,
} from '@carboncopyco/data';
import type { EnhancedPresentation } from '@carboncopyco/types';
import {
  Badge,
  Container,
  Separator,
  SkeletonRowLoading,
  TextBase,
  TooltipBubble,
  UserBasicInfo,
} from '@carboncopyco/ui';
import { MMMDoYYYY, dateUTCFormat } from '@carboncopyco/utils';
import type React from 'react';
import { type Col, FixedTable } from '../../../FixedTable';
import { Screenshot } from '../../../Screenshot';
import { useControlledFetch } from '../../Controls/useControlledFetch';
import { EmptyState } from '../../EmptyState';
import { handleSinglePlural } from '../../utils';
import { useDemoPageProps } from '../DemoPageContext';
import { ArchivedRowButtons } from './ArchivedRowButtons';
import { PresentationsControls } from './PresentationsControls';

const cols: Col<EnhancedPresentation>[] = [
  {
    alignment: 'left',
    title: 'Opportunity',
    width: 220,
    maxContentWidth: 200,
    render: (presentation) => {
      return (
        <div className="flex items-center justify-between gap-3 w-full">
          <div className="w-14 h-9 shrink-0">
            <Screenshot
              image={presentation.image}
              alt="demo"
              rounded="sm"
            />
          </div>
          <div className="flex flex-col flex-grow overflow-hidden">
            <TooltipBubble
              placement="top-start"
              title={presentation.forClient}
              disabled={presentation.forClient.length < 18}
            >
              <div className="overflow-hidden truncate">
                <TextBase
                  ellipsis
                  color="text-ds-natural-600"
                  label={presentation.forClient}
                />
              </div>
              <div className="flex gap-1">
                <Badge
                  label={`V${String(presentation.revisionId + 1)}`}
                  variant="grey"
                />
              </div>
            </TooltipBubble>
          </div>
        </div>
      );
    },
  },
  {
    title: 'Date',
    alignment: 'left',
    width: 120,
    maxContentWidth: 116,
    render: ({ createTime }) => {
      return (
        <TooltipBubble
          title={`Created on ${dateUTCFormat(createTime, 'MMM DD, YYYY [at] h:mma')}`}
          placement="top-start"
        >
          <TextBase
            color="text-ds-natural-300"
            label={`${MMMDoYYYY(createTime)}`}
          />
        </TooltipBubble>
      );
    },
  },
  {
    alignment: 'left',
    title: 'Creator',
    width: 130,
    maxContentWidth: 120,
    render: ({ creator }) => (
      <div className="flex items-center">
        <UserBasicInfo
          size="small"
          user={creator}
          textColor="text-ds-natural-600"
          truncateWidth={110}
          tooltip={creator.name.length > 15}
        />
      </div>
    ),
  },

  {
    alignment: 'right',
    title: 'Presented',
    width: 85,
    render: ({ totalPresentationViews }) => (
      <TooltipBubble
        title={handleSinglePlural(totalPresentationViews, 'Presented')}
        placement="top"
      >
        <TextBase
          label={totalPresentationViews || '-'}
          color="text-ds-natural-600"
        />
      </TooltipBubble>
    ),
  },
  {
    alignment: 'right',
    title: 'Shared',
    width: 85,
    render: ({ totalShares }) => (
      <TooltipBubble
        title={handleSinglePlural(totalShares, 'Shared')}
        placement="top"
      >
        <TextBase
          label={totalShares || '-'}
          color="text-ds-natural-600"
        />
      </TooltipBubble>
    ),
  },
  {
    alignment: 'right',
    title: 'Visited',
    width: 85,
    render: ({ totalShareViews }) => {
      return (
        <TooltipBubble
          title={handleSinglePlural(totalShareViews, 'Visited')}
          placement="top"
        >
          <TextBase
            label={totalShareViews || '-'}
            color="text-ds-natural-600"
          />
        </TooltipBubble>
      );
    },
  },
  {
    alignment: 'right',
    title: '',
    width: 230,
    render: (presentation) => (
      <ArchivedRowButtons presentation={presentation} />
    ),
  },
];

export const ArchivedPresentations = () => {
  const { organizationId, demoId, workspaceId } = useDemoPageProps();

  const {
    items: archivedPresentations,
    fetchNextControlledBatch,
    isLoading,
    isLoadingBatch,
    error,
  } = useControlledFetch<EnhancedPresentation, GetPresentationsByDemoArgs>({
    lazyHook: useLazyGetPresentationsByDemoQuery,
    extraEndpointArgs: {
      organizationId,
      workspaceId,
      demoId,
      deletedEntities: true,
    },
    mapUrlStateToEndpointArgs: {
      sort: 'sort',
      creators: 'creators',
      companies: 'companies',
      query: 'query',
    },
  });

  const RowWrapper = ({ children }: { children?: React.ReactNode }) => (
    <div className="flex flex-col">
      <div className="py-4">{children}</div>
      <Separator horizontal />
    </div>
  );

  if (!isLoading && !archivedPresentations?.length) {
    return (
      <EmptyState
        title="No archived presentations"
        description="No presentations have been archived yet."
      />
    );
  }

  return (
    <>
      <PresentationsControls isArchive />
      <div className="py-5">
        <FixedTable<EnhancedPresentation>
          error={error}
          data={archivedPresentations ?? []}
          cols={cols}
          isLoading={isLoading}
          handleFetchNextBatch={fetchNextControlledBatch}
          isLoadingBatch={isLoadingBatch}
          sideSpacing
          keyName="id"
          infiniteListMaxHeight={800}
          rowWrapper={RowWrapper}
          loader={
            <Container>
              <SkeletonRowLoading
                medium
                itemsCount={5}
              />
            </Container>
          }
        />
      </div>
    </>
  );
};
